import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MessageSquare } from 'lucide-react';
import chatService from '../services/chatService';

export default function ContactCreativeButton({ 
  currentUserId, 
  creativeUserId, 
  label = 'Message', 
  className = '', 
  icon = null,
  variant = 'default' // 'default', 'primary', 'outline'
}) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const resolveCurrentUserId = () => {
    if (currentUserId) return currentUserId;
    try { 
      const raw = localStorage.getItem('auth:user'); 
      if (raw) { 
        const parsed = JSON.parse(raw); 
        return parsed?.id || null; 
      } 
    } catch (e) { void e; }
    return null;
  };

  const startChat = async () => {
    const meId = resolveCurrentUserId();
    if (!meId || !creativeUserId) return;
    setLoading(true);
    setError('');
    try {
      const chat = await chatService.findOrCreateChat(meId, creativeUserId);
      const chatId = chat?.id || chat?.chat_id || chat?.data?.id;
      if (chatId) {
        navigate(`/dashboard/messages?chatId=${encodeURIComponent(chatId)}`);
      } else {
        setError('Could not open chat');
      }
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || 'Failed to start chat');
    } finally {
      setLoading(false);
    }
  };

  // Determine button styles based on variant and className
  const baseStyles = 'inline-flex items-center justify-center gap-2 font-medium transition-colors disabled:opacity-50';
  const variantStyles = {
    default: 'btn btn-primary btn-sm',
    primary: 'bg-blue-600 text-white hover:bg-blue-700 rounded-lg px-4 py-2',
    outline: 'border border-card-border text-primary-text hover:bg-subtle-bg rounded-lg px-4 py-2',
  };

  // If className includes layout classes (w-full, py-*, etc.), use custom styles
  const hasCustomStyles = className.includes('w-full') || className.includes('py-') || className.includes('rounded-');
  const buttonClass = hasCustomStyles 
    ? `${baseStyles} ${className}` 
    : `${variantStyles[variant] || variantStyles.default} ${className}`;

  // Determine wrapper styles - use full width when button is full width
  const isFullWidth = className.includes('w-full');
  const wrapperClass = isFullWidth 
    ? 'flex flex-col items-stretch gap-1 w-full' 
    : 'inline-flex flex-col items-start gap-1';

  const IconComponent = icon || <MessageSquare size={18} />;

  return (
    <div className={wrapperClass}>
      <button 
        type="button" 
        className={buttonClass}
        disabled={!creativeUserId || loading} 
        onClick={startChat}
      >
        {loading ? (
          'Opening…'
        ) : (
          <>
            {icon !== false && IconComponent}
            {label}
          </>
        )}
      </button>
      {error && <span className="text-xs text-rose-600">{error}</span>}
    </div>
  );
}
